import { useState } from 'react';
import { useQuery } from 'react-query';
import feathersClient from '../apiClient';
import { useUser } from '../apiClient/ApiContext';
import moment, { Moment } from 'moment';
import { CompanyData } from '../types/TymbeApi';
import {AdminPopup, AdminPopupDisplayFrequencyDaysEnum} from '@tymbe/schema/admin-popup.interface';
import {
  getPopupsForCompanyIds,
  getPopupsForModule,
  isNowWithinPopupTimeInterval,
  UseAdminPopupOptions,
} from '../pages/administration/adminPopupUtils';
import { Roles } from '../utils/enums';
import getResponseArray from '@tymbe/utils/getResponseArray';

type useAdminPopupReturnType = {
  showPopup: boolean;
  popupData: AdminPopup | null;
  handleClosePopup: () => void;
};

/**
 * Fetches admin popups for the given module and user's companies, selects the newest (highest ID) popup,
 * and shows it if:
 *   - The current time is inside the popup's from-to interval
 *   - If display_frequency_weekday is set, today matches the specified day
 *   - The popup's ID is greater than the one stored in localStorage (or not stored)
 *     for the relevant module.
 *
 * Handles modal state and localStorage updates for popup display.
 */
export function useAdminPopup({ module }: UseAdminPopupOptions): useAdminPopupReturnType {
  const user = useUser();
  const [showPopup, setShowPopup] = useState(false);
  const [popupData, setPopupData] = useState<AdminPopup | null>(null);
  const popupsResponse = useQuery(
    ['admin-popup'],
    async () =>
      getResponseArray<AdminPopup>(
        await feathersClient.service('admin-popup').find({
          query: {
            $or: [{ to: { $gte: moment().toISOString() } }, { to: { $null: true } }],
            $limit: -1,
          },
        })
      ),
    { staleTime: Infinity }
  );

  const localStorageKey = `admin_popup_${module}`;

  const handleClosePopup = () => {
    setShowPopup(false);
    setPopupData(null);
  };

  const handleOnceFrequency = (popup: AdminPopup, localStorageKey: string) => {
    const popupInLs = Number(localStorage.getItem(`${localStorageKey}_${popup.id}`));
    if (!popupInLs) {
      setPopupData(popup);
      setShowPopup(true);
      localStorage.setItem(`${localStorageKey}_${popup.id}`, String(popup.id));
    }
  };

  const handleDailyFrequency = (popup: AdminPopup, localStorageKey: string, now: Moment) => {
    const popupDateInLs = String(localStorage.getItem(`${localStorageKey}_${popup.id}`));
    const localPopupDate = moment(popupDateInLs);
    if (!localPopupDate.isSame(now, 'day')) {
      setPopupData(popup);
      setShowPopup(true);
      localStorage.setItem(`${localStorageKey}_${popup.id}`, String(now.toISOString()));
    }
  };

  const handleWeeklyFrequency = (popup: AdminPopup, localStorageKey: string, now: Moment) => {
    const popupDateInLs = String(localStorage.getItem(`${localStorageKey}_${popup.id}`));
    const localPopupDate = moment(popupDateInLs);

    if (!popup.display_frequency_weekday?.includes(now.day())) {
      return;
    }

    if (now.day() !== localPopupDate.day()) {
      setPopupData(popup);
      setShowPopup(true);
      localStorage.setItem(`${localStorageKey}_${popup.id}`, String(now.toISOString()));
    }
  };

  const hasUserTymbeRoles = user.hasRoles([Roles.SUPER_ADMIN, Roles.TYMBE_ADMIN, Roles.ADMIN, Roles.TYMBE_COORDINATOR]);

  if (hasUserTymbeRoles || !popupsResponse.data || !user?.person?.company) {
    return { showPopup, popupData, handleClosePopup };
  }

  const popups = Array.isArray(popupsResponse.data) ? popupsResponse.data : [];

  const popupsForModule = getPopupsForModule(popups, module);
  if (!popupsForModule.length) return { showPopup, popupData, handleClosePopup };

  const userCompanyIds = user.person.company.map((c: CompanyData) => c.id);
  const popupsForCompanies = getPopupsForCompanyIds(popupsForModule, userCompanyIds);
  if (!popupsForCompanies.length) return { showPopup, popupData, handleClosePopup };

  const now = moment();

  // In case of collision of 2 or more popups, show the newest created_at one
  const currentlyActivePopups = popupsForCompanies
    .filter(isNowWithinPopupTimeInterval)
    .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

  for (const popup of currentlyActivePopups) {
    switch (popup.display_frequency) {
      case AdminPopupDisplayFrequencyDaysEnum.once:
        handleOnceFrequency(popup, localStorageKey);
        break;
      case AdminPopupDisplayFrequencyDaysEnum.daily:
        handleDailyFrequency(popup, localStorageKey, now);
        break;
      case AdminPopupDisplayFrequencyDaysEnum.weekly:
        handleWeeklyFrequency(popup, localStorageKey, now);
        break;
    }
  }

  return { showPopup, popupData, handleClosePopup };
}
